import React from 'react';

const LegalPage = ({ title, lastUpdated, children }) => (
  <div className="py-20 px-6">
    <div className="max-w-3xl mx-auto">
      <span className="font-label text-[var(--color-coral)] mb-3 inline-block">LEGAL</span>
      <h1 className="font-serif text-3xl md:text-4xl mb-2">{title}</h1>
      <p className="text-xs text-[var(--color-muted)] mb-10">Last updated: {lastUpdated}</p>
      <div className="prose prose-sm max-w-none text-sm leading-relaxed [&_h2]:font-serif [&_h2]:text-xl [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:font-display [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-[var(--color-muted)] [&_p]:mb-4 [&_ul]:text-[var(--color-muted)] [&_li]:mb-1 [&_strong]:text-[var(--color-charcoal)]">
        {children}
      </div>
    </div>
  </div>
);

export const PrivacyPage = () => (
  <LegalPage title="Privacy Policy" lastUpdated="February 2026">
    <h2>1. Introduction</h2>
    <p>Stilos ("we," "us," or "our"), a product of Themelio, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service at stilos.my.</p>
    <p>We are based in the European Union and fully comply with GDPR (General Data Protection Regulation). By using Stilos, you consent to the data practices described in this policy.</p>

    <h2>2. Information We Collect</h2>
    <h3>2.1 Account Information</h3>
    <p>When you create an account, we collect your email address, full name, company name (optional), and encrypted password.</p>
    <h3>2.2 Content Data</h3>
    <p>We store the content you create using Stilos, including articles, voice recordings (temporarily during processing), brand voice profiles, and brand reports. <strong>Your content is your property and is never used to train AI models.</strong></p>
    <h3>2.3 Usage Data</h3>
    <p>We collect anonymized usage data such as feature usage frequency, credit consumption, and general interaction patterns to improve the service.</p>
    <h3>2.4 Payment Information</h3>
    <p>Payment processing is handled by Stripe. We do not store your credit card details. Stripe's privacy policy applies to payment data.</p>

    <h2>3. How We Use Your Information</h2>
    <ul>
      <li>To provide and maintain the Stilos service</li>
      <li>To process your transactions and manage your account</li>
      <li>To send transactional emails (confirmations, password resets, billing)</li>
      <li>To improve and personalize your experience</li>
      <li>To comply with legal obligations</li>
    </ul>
    <p><strong>We do NOT:</strong></p>
    <ul>
      <li>Sell your personal data to third parties</li>
      <li>Use your content to train AI models</li>
      <li>Share your data with advertising networks</li>
    </ul>

    <h2>4. Third-Party Services</h2>
    <p>We use the following third-party services to operate Stilos:</p>
    <ul>
      <li><strong>OpenAI (GPT-5.2, Whisper)</strong> — for AI content generation and voice transcription. OpenAI does not retain data submitted via API for model training.</li>
      <li><strong>Stripe</strong> — for payment processing</li>
      <li><strong>Resend</strong> — for transactional emails</li>
      <li><strong>MongoDB Atlas</strong> — for data storage (encrypted at rest)</li>
    </ul>

    <h2>5. Data Retention</h2>
    <p>We retain your account data and content for as long as your account is active. If you delete your account, we will delete your data within 30 days, except where we are required to retain it for legal or regulatory purposes.</p>
    <p>When you delete your account we erase your personal data, but we retain records of your payments (amount, date, invoice/transaction identifiers) in pseudonymized form — no longer linked to your profile — for 5 years, as required by Greek tax and accounting law (L.4308/2014). After that period these records are permanently purged.</p>

    <h2>6. Your Rights (GDPR)</h2>
    <p>Under GDPR, you have the right to:</p>
    <ul>
      <li><strong>Access</strong> — Request a copy of your personal data</li>
      <li><strong>Rectification</strong> — Correct inaccurate data</li>
      <li><strong>Erasure</strong> — Request deletion of your data</li>
      <li><strong>Portability</strong> — Export your data in a machine-readable format</li>
      <li><strong>Object</strong> — Object to processing of your data</li>
      <li><strong>Restriction</strong> — Request limitation of processing</li>
    </ul>
    <p>To exercise any of these rights, contact us at privacy@stilos.my.</p>

    <h2>7. Security</h2>
    <p>We implement industry-standard security measures including encryption in transit (TLS 1.3), encryption at rest (AES-256), JWT authentication, and regular security audits.</p>

    <h2>8. Contact</h2>
    <p>For privacy-related questions: <strong>privacy@stilos.my</strong></p>
    <p>Data Protection Officer: Themelio DPO, European Union</p>
  </LegalPage>
);

export const TermsPage = () => (
  <LegalPage title="Terms of Service" lastUpdated="February 2026">
    <h2>1. Acceptance of Terms</h2>
    <p>By accessing or using Stilos (stilos.my), you agree to be bound by these Terms of Service. If you do not agree, do not use the service.</p>

    <h2>2. Description of Service</h2>
    <p>Stilos is an AI-powered content creation platform that helps users generate, optimize, and publish content. The service includes AI article generation, voice-to-article transcription, SEO/AEO/GEO analysis, brand voice profiles, brand reporting, and publishing integrations.</p>

    <h2>3. User Accounts</h2>
    <p>You must provide accurate information when creating an account. You are responsible for maintaining the security of your account credentials. You must be at least 18 years old to use Stilos.</p>

    <h2>4. Content Ownership</h2>
    <p><strong>You own all content you create using Stilos.</strong> We do not claim any ownership or intellectual property rights over your articles, brand profiles, reports, or any other content you generate. You grant us a limited license to store and process your content solely to provide the service.</p>

    <h2>5. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use Stilos to generate harmful, misleading, or illegal content</li>
      <li>Attempt to reverse-engineer or circumvent security measures</li>
      <li>Share your account with others or resell access</li>
      <li>Exceed fair usage limits intentionally</li>
    </ul>

    <h2>6. Subscriptions & Billing</h2>
    <p>Paid plans are billed monthly or annually. You can cancel at any time — your access continues until the end of the billing period. Refunds are handled on a case-by-case basis. Credit packs are non-refundable once credits have been used.</p>

    <h2>7. AI-Generated Content</h2>
    <p>Content generated by Stilos is created using AI and should be reviewed before publishing. We do not guarantee factual accuracy of AI-generated content. You are responsible for reviewing and fact-checking content before publication.</p>

    <h2>8. Service Availability</h2>
    <p>We strive for 99.9% uptime but do not guarantee uninterrupted service. Maintenance windows will be communicated in advance when possible.</p>

    <h2>9. Limitation of Liability</h2>
    <p>Stilos is provided "as is." We are not liable for indirect, incidental, or consequential damages arising from your use of the service.</p>

    <h2>10. Modifications</h2>
    <p>We may update these terms from time to time. Material changes will be communicated via email or in-app notification. Continued use after changes constitutes acceptance.</p>

    <h2>11. Governing Law</h2>
    <p>These terms are governed by the laws of the European Union. Any disputes will be resolved in EU courts.</p>

    <h2>12. Contact</h2>
    <p>Questions about these terms: <strong>legal@stilos.my</strong></p>
  </LegalPage>
);

export const CookiePage = () => (
  <LegalPage title="Cookie Policy" lastUpdated="February 2026">
    <h2>1. What Are Cookies</h2>
    <p>Cookies are small text files stored on your device when you visit a website. They help us provide a better experience by remembering your preferences and understanding how you use Stilos.</p>

    <h2>2. Cookies We Use</h2>
    <h3>Essential Cookies (Required)</h3>
    <p>These cookies are necessary for Stilos to function. They include authentication tokens and session management. You cannot opt out of essential cookies.</p>

    <h3>Analytics Cookies (Optional)</h3>
    <p>We use anonymized analytics to understand how users interact with Stilos. No personal information is collected through analytics cookies.</p>

    <h2>3. Third-Party Cookies</h2>
    <p>We use Stripe for payment processing, which may set its own cookies. These are governed by Stripe's cookie policy.</p>

    <h2>4. Managing Cookies</h2>
    <p>You can manage cookies through your browser settings. Disabling essential cookies may prevent Stilos from functioning correctly.</p>

    <h2>5. Contact</h2>
    <p>Questions about cookies: <strong>privacy@stilos.my</strong></p>
  </LegalPage>
);

export const DpaPage = () => (
  <LegalPage title="Data Processing Agreement" lastUpdated="February 2026">
    <h2>1. Scope</h2>
    <p>This Data Processing Agreement (DPA) supplements our Privacy Policy and Terms of Service. It applies to the processing of personal data by Stilos on behalf of our users, in compliance with GDPR Article 28.</p>

    <h2>2. Definitions</h2>
    <ul>
      <li><strong>Controller:</strong> You, the user of Stilos</li>
      <li><strong>Processor:</strong> Stilos (Themelio)</li>
      <li><strong>Sub-processors:</strong> Third-party services used by Stilos</li>
    </ul>

    <h2>3. Data Processing Details</h2>
    <p><strong>Purpose:</strong> To provide AI-powered content creation, optimization, and publishing services.</p>
    <p><strong>Categories of data:</strong> Account information, user-generated content, usage analytics.</p>
    <p><strong>Data subjects:</strong> Registered users of Stilos.</p>
    <p><strong>Duration:</strong> For the lifetime of the user's account, plus 30 days after deletion.</p>

    <h2>4. Sub-processors</h2>
    <p>We use the following sub-processors:</p>
    <ul>
      <li><strong>OpenAI</strong> (USA) — AI content generation. Data processed under Standard Contractual Clauses.</li>
      <li><strong>Stripe</strong> (USA) — Payment processing. PCI-DSS compliant.</li>
      <li><strong>Resend</strong> (USA) — Transactional email. Data processed under SCCs.</li>
      <li><strong>MongoDB Atlas</strong> — Data storage. EU-region deployment available.</li>
    </ul>

    <h2>5. Security Measures</h2>
    <p>We implement: encryption in transit and at rest, access controls, regular security audits, incident response procedures, and data backup protocols.</p>

    <h2>6. Data Breach Notification</h2>
    <p>In the event of a data breach, we will notify affected users within 72 hours of becoming aware of the breach, as required by GDPR Article 33.</p>

    <h2>7. Contact</h2>
    <p>Data processing inquiries: <strong>dpo@stilos.my</strong></p>
  </LegalPage>
);

export const ImpressumPage = () => (
  <LegalPage title="Impressum" lastUpdated="February 2026">
    <h2>Information according to EU regulations</h2>

    <h3>Company</h3>
    <p><strong>Themelio</strong><br />
    European Union</p>

    <h3>Product</h3>
    <p><strong>Stilos</strong> — AI-Powered Content Creation Platform<br />
    Website: stilos.my</p>

    <h3>Contact</h3>
    <p>Email: hello@stilos.my<br />
    Support: support@stilos.my<br />
    Privacy: privacy@stilos.my</p>

    <h3>Responsible for Content</h3>
    <p>Content responsibility according to applicable regulations lies with Themelio.</p>

    <h3>Dispute Resolution</h3>
    <p>The European Commission provides an online dispute resolution platform at <a href="https://ec.europa.eu/consumers/odr" className="text-[var(--color-coral)]" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a>.</p>
    <p>We are not obligated or willing to participate in dispute resolution proceedings before a consumer arbitration board.</p>

    <h3>Liability for Content</h3>
    <p>As a service provider, we are responsible for our own content on these pages under applicable laws. However, we are not obligated to monitor transmitted or stored third-party information.</p>
  </LegalPage>
);
